import { EventEmitter } from "events";
import EngagementService from "../services/EngagementService";
import StoreContainer from "../stores/StoreContainer";
import CallService from "../services/CallService";

import {
  EngagementRequest,
  EngagementSettings,
  EngagementContentType,
  SurveyType,
  SubmitSurveyRequest,
  SurveyContent,
  KBArticle,
  GroupAvailabilityStatus,
  UnavailableAction,
  EngagementContent,
  PreEngagementRequest,
  SurveyAnswer
} from "../types";
import ReferralService from "../services/ReferralService";
import { logInfo, logError } from "../helpers/eventLogger";
import CookieStore from "./CookieStore";

class EngagementStore {
  private _emitter = new EventEmitter();
  private _preEngagementId: string;
  private _engagementId: string;
  private _settings: EngagementSettings;
  private _prechatSurveyContent: SurveyContent;
  private _postchatSurveyContent: SurveyContent;
  private _unavailableSurveyContent: SurveyContent;
  private _prechatSurveyAnswers: Array<any>;
  private _cookieStore: CookieStore;

  constructor(cookieStore: CookieStore) {
    this._cookieStore = cookieStore;
    this._preEngagementId = this._cookieStore.get("preEngagementId");
    this._engagementId = this._cookieStore.get("engagementId");
  }

  async loadSettings() {
    this._settings = await EngagementService.loadSettings(
      StoreContainer.SettingStore.getSiteId(),
      StoreContainer.GroupStore.getActiveOrPageGroupId()
    );
  }

  onReceiveIntroForm(callback: () => void) {
    this._emitter.addListener("receiveIntroForm", callback);
  }

  onReceiveUnavailableHtml(callback: (html: string) => void) {
    this._emitter.addListener("receiveUnavailableHtml", callback);
  }

  onReceiveUnavailableUrl(callback: (url: string) => void) {
    this._emitter.addListener("receiveUnavailableUrl", callback);
  }

  onReceivePreChatSurvey(callback: () => void) {
    this._emitter.addListener("receivePreChatSurvey", callback);
  }

  onReceivePostChatSurvey(callback: (content: any) => void) {
    this._emitter.addListener("receivePostChatSurvey", callback);
  }

  onReceiveReferralContent(callback: () => void) {
    this._emitter.addListener("receiveReferralContent", callback);
  }

  onChatClosed(callback: () => void) {
    this._emitter.addListener("onChatClosed", callback);
  }

  onLoading(callback: () => void) {
    this._emitter.addListener("onLoading", callback);
  }

  onReceiveUnavailableSurvey(callback: () => void) {
    this._emitter.addListener("receiveUnavailableSurvey", callback);
  }

  onPostchatSurveySubmitted(callback: () => void) {
    this._emitter.addListener("postchatSurveySubmitted", callback);
  }

  onUnavailableSurveySubmitted(callback: () => void) {
    this._emitter.addListener("unavailableSurveySubmitted", callback);
  }

  onReceiveKnowledgeBaseArticles(
    callback: (articles: Array<KBArticle>) => void
  ) {
    this._emitter.addListener("receiveKnowledgeBaseArticles", callback);
  }

  onReceiveError(callback: (errorMessage: string) => void) {
    this._emitter.addListener("receiveError", callback);
  }

  removeAllListeners() {
    this._emitter.removeAllListeners();
  }

  isChatVisible() {
    return (
      this.hasEngagementId() ||
      StoreContainer.AvailabilityStore.isChatAvailableInternal() ||
      !this._settings.HideWhenUnavailable
    );
  }

  hasEngagementId() {
    return !!this._engagementId;
  }

  hasPreEngagementId() {
    return !!this._preEngagementId;
  }

  hasEventTracking() {
    return this._settings.HasEventTracking;
  }

  getEngagementId() {
    return this._engagementId;
  }

  getPreEngagementId() {
    return this._preEngagementId;
  }

  getPrechatSurveyAnswers() {
    return this._prechatSurveyAnswers;
  }

  getPrechatSurveyContent() {
    if (!this._prechatSurveyContent) {
      throw new Error("The prechat survey content has not been loaded");
    }

    return this._prechatSurveyContent;
  }

  getPostchatSurveyContent() {
    if (!this._postchatSurveyContent) {
      throw new Error("The postchat survey content has not been loaded");
    }

    return this._postchatSurveyContent;
  }

  getUnavailableSurveyContent() {
    if (!this._unavailableSurveyContent) {
      throw new Error("The unavailable survey content has not been loaded");
    }

    return this._unavailableSurveyContent;
  }

  async submitPostchatSurvey(answers: any) {
    const payload: SubmitSurveyRequest = {
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      SiteID: StoreContainer.SettingStore.getSiteId(),
      VisitorID: StoreContainer.VisitorStore.getVisitorId(),
      EngagementID: this.hasEngagementId() ? this.getEngagementId() : "",
      CallID: StoreContainer.CallStore.getCallId()
        ? StoreContainer.CallStore.getCallId()
        : "",
      SurveyID: this._postchatSurveyContent.Survey.SurveyID,
      SurveyType: SurveyType.Postchat,
      Answers: answers,
      GroupAvailabilityStatus:
        StoreContainer.AvailabilityStore.getGroupAvailability()
    };

    this._emitter.emit("onLoading");
    await EngagementService.submitSurvey(
      StoreContainer.SettingStore.getSiteId(),
      payload
    );
    await this.handleReferral(async (hadFrameContent: boolean) => {
      await this.clearEngagement();
      await StoreContainer.CallStore.clearCallInfo();
      if (!hadFrameContent) {
        this._emitter.emit("postchatSurveySubmitted");
      }
    });
  }

  async submitUnavailableSurvey(answers: any) {
    const payload: SubmitSurveyRequest = {
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      SiteID: StoreContainer.SettingStore.getSiteId(),
      VisitorID: StoreContainer.VisitorStore.getVisitorId(),
      EngagementID: this._preEngagementId || this._engagementId,
      CallID: StoreContainer.CallStore.getCallId()
        ? StoreContainer.CallStore.getCallId()
        : "",
      SurveyID: this._unavailableSurveyContent.Survey.SurveyID,
      SurveyType: SurveyType.Unavailable,
      Answers: answers,
      GroupAvailabilityStatus:
        StoreContainer.AvailabilityStore.getGroupAvailability()
    };

    this._emitter.emit("onLoading");
    await EngagementService.submitSurvey(
      StoreContainer.SettingStore.getSiteId(),
      payload
    );
    await this.clearEngagement();
    await StoreContainer.CallStore.clearCallInfo();
    this._emitter.emit("unavailableSurveySubmitted");
  }

  async submitPrechatSurvey(answers: Array<any>) {
    this._prechatSurveyAnswers = answers;

    logInfo("submitting prechat survey");

    if (StoreContainer.SettingStore.getClickToCall()) {
      await this.beginClickToCall(answers, "");
    } else if (this.shouldSearchKnowledgeBase(answers)) {
      await this.searchKnowledgeBase(answers);
    } else {
      await this.beginChatFromPrechatSurvey(answers);
    }
  }

  async continueFromKnowledgeBase() {
    await this.beginChatFromPrechatSurvey(this._prechatSurveyAnswers);
  }

  shouldSearchKnowledgeBase(answers: Array<any>) {
    return answers.some((x) => x.KBSearchQuestion);
  }

  async searchKnowledgeBase(answers: Array<any>) {
    const kbAnswers = answers
      .filter((x) => x.KBSearchQuestion)
      .map((x) => x.Answer);

    const response = (await EngagementService.getKBArticles(
      StoreContainer.SettingStore.getSiteId(),
      kbAnswers
    )) as Array<any>;

    const articles: Array<KBArticle> = [];

    response.forEach((obj) => {
      obj.Articles.forEach((article: any) => {
        articles.push({
          name: article.Name,
          url: article.Url
        });
      });
    });

    if (articles.length) {
      this._emitter.emit("receiveKnowledgeBaseArticles", articles);
    } else {
      await this.beginChatFromPrechatSurvey(answers);
    }
  }

  getPrechactSurveyId() {
    if (!this._prechatSurveyContent || !this._prechatSurveyContent.Survey) {
      return null;
    }

    return this._prechatSurveyContent.Survey.SurveyID;
  }

  async beginClickToCall(answers: SurveyAnswer[], directPhoneNumber: string) {
    const callRequest = StoreContainer.CallStore.createCallRequest();

    callRequest.DirectPhoneNumber = directPhoneNumber;

    callRequest.VisitorInfo = StoreContainer.VisitorStore.getVisitorInfo();
    callRequest.FromEngagementId =
      StoreContainer.EngagementStore.getEngagementId();
    callRequest.SurveyResults = {
      Answers: answers,
      EngagementID: "",
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      SiteID: StoreContainer.SettingStore.getSiteId(),
      SurveyID: this.getPrechactSurveyId(),
      SurveyType: SurveyType.Prechat,
      VisitorID: StoreContainer.VisitorStore.getVisitorId(),
      ClickToCall: true
    };

    const content = await CallService.requestCall(
      StoreContainer.SettingStore.getSiteId(),
      callRequest
    );
    this.setPreEngagementId(undefined);

    if (content.IsUnavailable) {
      await StoreContainer.ChatStore.handleReceiveEngagementContent(
        content.Result
      );
      return;
    }

    StoreContainer.CallStore.setCallId(content.Call.CallId);
    StoreContainer.CallStore.handleCallWaiting(content.Call);
    StoreContainer.CallStore.connect();
  }

  async beginChatFromPrechatSurvey(answers: any) {
    const engagementRequest = this.createEngagementRequest();

    engagementRequest.SurveyResults = {
      Answers: answers,
      EngagementID: "",
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      SiteID: StoreContainer.SettingStore.getSiteId(),
      SurveyID: this._prechatSurveyContent.Survey.SurveyID,
      SurveyType: SurveyType.Prechat,
      VisitorID: StoreContainer.VisitorStore.getVisitorId()
    };

    logInfo("beginning chat with survey");

    await this.beginChat(engagementRequest);
  }

  async sendIntroMessage(introMessage: string) {
    const engagementRequest = this.createEngagementRequest();
    engagementRequest.IntroMessage = introMessage;

    logInfo("beginning chat with intro message");

    await this.beginChat(engagementRequest);
  }

  async beginBotChat() {
    const engagementRequest = this.createEngagementRequest();

    logInfo("beginning bot chat");

    await this.beginChat(engagementRequest);
  }

  async beginChatWithoutPrechat() {
    const engagementRequest = this.createEngagementRequest();
    engagementRequest.SkippedPrechat = true;

    logInfo("skipping prechat and starting chat");

    await this.beginChat(engagementRequest);
  }

  async beginChat(engagementRequest: EngagementRequest) {
    const content = await this.loadContent(engagementRequest);
    if (!content) {
      return;
    }
    //Clear initial message for inline invite to prevent errent pre-chat information
    StoreContainer.InviteStore.setInviteAcceptMessage(null);
    await StoreContainer.ChatStore.handleReceiveEngagementContent(content);
  }

  async startEngagement() {
    if (this.hasEngagementId()) {
      const resumed = await this.resumeChat();

      if (!resumed) {
        await this.loadPreEngagementId();
        await this.emitPrechatEvent();
      }

      return;
    }

    if (!this.hasEngagementId() && !this.hasPreEngagementId()) {
      await this.loadPreEngagementId();
      await this.emitPrechatEvent();
      return;
    }

    if (this.hasPreEngagementId()) {
      await this.emitPrechatEvent();
      return;
    }
  }

  async clearEngagement() {
    await this.setEngagementGroupId(undefined);
    this.setEngagementId(undefined);
    this.setPreEngagementId(undefined);
    StoreContainer.ChatStore.clearChatInfo();
    StoreContainer.FrameStore.preventOpeningOnReload();
    logInfo("cleared engagement");
  }

  async handleNoContent() {
    await this.clearEngagement();
    this._emitter.emit("onChatClosed");
    StoreContainer.FrameStore.collapseOrClose();
    logInfo("handled no content");
  }

  async handleReferral(callback: (hadFrameContent: boolean) => Promise<void>) {
    await ReferralService.openReferralWindow();
    const hasFrameContent =
      await ReferralService.shouldShowReferrralContentInFrame();
    if (hasFrameContent) {
      this._emitter.emit("receiveReferralContent");
    }
    callback(hasFrameContent);
  }

  async handlePrechatEvent(contentType: EngagementContentType, content: any) {
    if (contentType === EngagementContentType.PostChatSurvey && !content) {
      await this.handleReferral(async (hadFrameContent: boolean) => {
        if (!hadFrameContent) {
          await this.handleNoContent();
        }
      });
      return;
    }

    const isPrechatContent =
      contentType === EngagementContentType.PrechatSurvey ||
      contentType === EngagementContentType.FakeChat;

    const shouldSkipFakeChat =
      contentType === EngagementContentType.FakeChat &&
      StoreContainer.InviteStore.hasInviteAcceptMessage();

    if (
      shouldSkipFakeChat ||
      (isPrechatContent && StoreContainer.SettingStore.shouldSkipPrechat())
    ) {
      await this.beginChatWithoutPrechat();
      return;
    }

    switch (contentType) {
      case EngagementContentType.UnavailableSurvey:
        this.setUnavailableSurveyContent(content);
        this._emitter.emit("receiveUnavailableSurvey", content);
        break;
      case EngagementContentType.UnavailableText:
        this._emitter.emit("receiveUnavailableHtml", content);
        break;
      case EngagementContentType.UnavailableUrl:
        // If we don't set isExpanded to false in storage, the user will never be able to
        // return to the page as they will be stuck in an infinite redirect loop as the
        // component is mounted upon return.
        this._cookieStore.set("isExpanded", false);
        this._emitter.emit("receiveUnavailableUrl", content);
        break;
      case EngagementContentType.PrechatSurvey:
        this.setPrechatSurveyContent(content);
        this._emitter.emit("receivePreChatSurvey", content);
        break;
      case EngagementContentType.PostChatSurvey:
        this.setPostchatSurveyContent(content);
        this._emitter.emit("receivePostChatSurvey", content);
        break;
      case EngagementContentType.BotChat:
        this.beginBotChat();
        break;
      case EngagementContentType.FakeChat:
      default:
        this._emitter.emit("receiveIntroForm");
        break;
    }
  }

  getMaxFileSizeBytes() {
    let maxFileSize = this._settings.MaxUploadFileSize;

    if (!maxFileSize) {
      maxFileSize = 20;
    }

    return maxFileSize * 1000000;
  }

  getFileSizeErrorMessage() {
    return this._settings.FileUploadSizeErrorMessage;
  }

  isBotEnabled() {
    return this._settings.BotEnabled;
  }

  areAgentsPrioritized() {
    return this._settings.PrioritizeAgentsOverBots;
  }

  availabilityPingingEnabled() {
    return this._settings.EnableVisitorAvailabilityPing;
  }

  areManualInvitesEnabled() {
    return this._settings.ManualInvitesEnabled;
  }

  hasInvites() {
    return this._settings.HasInvites;
  }

  private async emitPrechatEvent() {
    StoreContainer.InviteStore.clearInvite();
    const content = this.getPrechatContent();
    await this.handlePrechatEvent(content.ContentType, content.Content);
  }

  private getPrechatContent() {
    return StoreContainer.AvailabilityStore.isChatAvailableInternal()
      ? this.getAvailablePrechatContent()
      : this.getUnavailablePrechatContent();
  }

  private getAvailablePrechatContent() {
    const settings = this._settings;
    if (settings.EnablePrechatSurvey) {
      return {
        Content: {
          Survey: settings.PrechatSurvey,
          HeaderText: settings.PrechatHeaderText,
          ButtonText: settings.PrechatSubmitText
        },
        ContentType: EngagementContentType.PrechatSurvey
      };
    } else if (settings.BotEnabled) {
      return {
        ContentType: EngagementContentType.BotChat
      };
    } else {
      return {
        Content: settings.CompanyLogo,
        ContentType: EngagementContentType.FakeChat
      };
    }
  }

  private getUnavailablePrechatContent() {
    const settings = this._settings;
    const unavailableText = this.getRelevantUnavailableText();

    switch (settings.UnavailableAction) {
      case UnavailableAction.Survey:
        return {
          Content: {
            Survey: settings.UnavailableSurvey,
            HeaderText: unavailableText.SurveyText,
            ButtonText: settings.UnavailableSubmitText,
            ConfirmationText:
              settings.UnavailableSurveySubmissionConfirmationText
          },
          ContentType: EngagementContentType.UnavailableSurvey
        };
      case UnavailableAction.JustText:
        return {
          Content: unavailableText.CustomText,
          ContentType: EngagementContentType.UnavailableText
        };
      case UnavailableAction.Html:
        return {
          Content: unavailableText.CustomHtml,
          ContentType: EngagementContentType.UnavailableText
        };
      case UnavailableAction.RedirectPage:
        return {
          Content: settings.UnavailableRedirectUrl,
          ContentType: EngagementContentType.UnavailableUrl
        };
    }
  }

  private getRelevantUnavailableText() {
    const settings = this._settings;
    switch (StoreContainer.AvailabilityStore.getGroupAvailability()) {
      case GroupAvailabilityStatus.OutOfSchedule:
        return {
          SurveyText: settings.OutOfScheduleSurveyText,
          CustomText: settings.OutOfScheduleCustomText,
          CustomHtml: settings.OutOfScheduleCustomHtml
        };
      case GroupAvailabilityStatus.AgentsBusy:
        return {
          SurveyText: settings.AgentsBusySurveyText,
          CustomText: settings.AgentsBusyCustomText,
          CustomHtml: settings.AgentsBusyCustomHtml
        };
      default:
        return {
          SurveyText: settings.UnavailableSurveyText,
          CustomText: settings.UnavailableCustomText,
          CustomHtml: settings.UnavailableCustomHtml
        };
    }
  }

  private getRequestedFrom() {
    const url = StoreContainer.FrameStore.getChatRequestedFromUrl();
    if (url) {
      return url;
    }

    try {
      if (!StoreContainer.FrameStore.isPopOut()) {
        return window.parent.window.location.href;
      }
    } catch (e) {}

    return "";
  }

  private createEngagementRequest() {
    const data: EngagementRequest = {
      SiteID: StoreContainer.SettingStore.getSiteId(),
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      VisitorID: StoreContainer.VisitorStore.getVisitorId(),
      Name: StoreContainer.VisitorStore.getVisitorName(),
      Email: StoreContainer.VisitorStore.getVisitorEmail(),
      EngagementID: this._engagementId,
      IsPolling: false,
      InviteConversionID: StoreContainer.InviteStore.getInviteConversionId(),
      RequestedFrom: this.getRequestedFrom(),
      GroupAvailabilityStatus: 3,
      PreEngagementId: this._preEngagementId,
      CustomData: StoreContainer.SettingStore.getCustomVars(),
      VisitorInfo: StoreContainer.VisitorStore.getVisitorInfo(),
      PreEngagementMessages:
        StoreContainer.InviteStore.getPreEngagementMessages(),
      VisitorLanguageDisplayName: StoreContainer.LanguageStore.getDisplayName(),
      IntroMessage: StoreContainer.InviteStore.getInviteAcceptMessage()
    };

    return data;
  }

  setEngagementId(engagementId: string) {
    this._engagementId = engagementId;
    this._cookieStore.set("engagementId", engagementId);
    this._emitter.emit("setEngagementId", engagementId);
  }

  setPreEngagementId(preEngagementId: string) {
    this._preEngagementId = preEngagementId;
    this._cookieStore.set("preEngagementId", preEngagementId);
  }

  async setEngagementGroupId(groupId: number) {
    this._cookieStore.set("engagement_groupId", groupId);
    await StoreContainer.GroupStore.changeActiveGroupId(groupId);
  }

  async refreshEngagement() {
    if (!this._engagementId) {
      return;
    }
    const engagementRequest = this.createEngagementRequest();

    logInfo("refreshing engagement");

    const content = await this.loadContent(engagementRequest);
    if (!content) {
      return;
    }

    await StoreContainer.ChatStore.loadMessages();

    await StoreContainer.ChatStore.handleReceiveEngagementContent(content);
  }

  onContentLoaded(callback: (content: EngagementContent) => void) {
    this._emitter.on("onContentLoaded", callback);
  }

  private setPrechatSurveyContent(prechatSurveyContent: SurveyContent) {
    this._prechatSurveyContent = prechatSurveyContent;
  }

  private setPostchatSurveyContent(postchatSurveyContent: SurveyContent) {
    this._postchatSurveyContent = postchatSurveyContent;
  }

  private setUnavailableSurveyContent(unavailableSurveyContent: SurveyContent) {
    this._unavailableSurveyContent = unavailableSurveyContent;
  }

  private async loadPreEngagementId() {
    const siteId = StoreContainer.SettingStore.getSiteId();

    const preEngagementRequest: PreEngagementRequest = {
      SiteID: siteId,
      GroupID: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      GroupAvailabilityStatus:
        StoreContainer.AvailabilityStore.getGroupAvailability(),
      ChatRequestedFromPage: this.getRequestedFrom(),
      VisitorInfo: StoreContainer.VisitorStore.getVisitorInfo()
    };

    const id = await EngagementService.loadPreEngagementId(
      siteId,
      preEngagementRequest
    );

    this.setPreEngagementId(id);
  }

  private checkForPrechatBug(engagementRequest: EngagementRequest) {
    const hasPrechatData =
      engagementRequest.IntroMessage || engagementRequest.SurveyResults;
    const hasEngagementId = engagementRequest.EngagementID;
    if (hasPrechatData && hasEngagementId) {
      logError("Visitor has an engagement id when submitting prechat");
    }
  }

  private applyLanguage(engagementRequest: EngagementRequest) {
    engagementRequest.VisitorLanguageDisplayName =
      StoreContainer.LanguageStore.getDisplayName();
  }

  private async loadContent(engagementRequest: EngagementRequest) {
    this.checkForPrechatBug(engagementRequest);
    this.applyLanguage(engagementRequest);

    let content: EngagementContent;

    try {
      content = await EngagementService.loadContent(
        StoreContainer.SettingStore.getSiteId(),
        engagementRequest
      );

      this._emitter.emit("onContentLoaded", content);
    } catch (e) {
      await this.handleEngagementRequestError(e);
      return null;
    }

    return content;
  }

  private async handleEngagementRequestError(e: any) {
    switch (e.statusCode) {
      case 403:
        //This visitor's IP is blocked. Forcibly hide chat from them.
        StoreContainer.FrameStore.setLauncherVisibility("hidden");
        StoreContainer.FrameStore.collapseOrClose();
        break;
      case 404:
        await StoreContainer.ChatStore.handleEngagementNotFound();
        break;
      case 500:
        //There was a problem. Show an error screen to the visitor.
        await this.clearEngagement();
        console.error(e.response.ExceptionMessage);
        logInfo("received a 500 from request engagement");
        this._emitter.emit("receiveError", e.response.ExceptionMessage);
        break;
    }
  }

  private contentIsUnavailable(content: EngagementContent) {
    if (!content) {
      return false;
    }

    const unavailableContentType = [
      EngagementContentType.UnavailableText,
      EngagementContentType.UnavailableSurvey,
      EngagementContentType.UnavailableUrl
    ];

    return unavailableContentType.indexOf(content.ContentType) !== -1;
  }

  private async resumeChat(): Promise<boolean> {
    const engagementRequest = this.createEngagementRequest();

    const content = await this.loadContent(engagementRequest);
    logInfo("resuming engagement");
    //Don't resume an unavailable survey if the group is now available. Just let them try chatting again.
    const resumingUnavailableContentWhileAvailable =
      this.contentIsUnavailable(content) &&
      (await StoreContainer.AvailabilityStore.isActiveGroupAvailable());

    if (!content || resumingUnavailableContentWhileAvailable) {
      await this.clearEngagement();
      return false;
    }

    await StoreContainer.ChatStore.handleReceiveEngagementContent(content);
    return true;
  }
}

export default EngagementStore;
